// 🏡🔍 The horizon line (PORTED_HOME_HANDOFF_2026_09_25 W2c): in a ported room,
// one quiet row in the owner's feed where the resident's view of the chat
// ends. Everything above it was not in the prompt his newest reply was
// written from. The boundary comes from the SERVER (the oldest row that
// prompt actually held, `{ id, at }` on the owner's why fetch), never from
// what this page happens to have loaded.
//
// Copy (🧠 wave 2 C2): the boundary carries `mem` from the server = every
// line above the edge lives on as one of his memories (portedMemory's fold
// moved its cursor past them). mem → "↑ above here, {Name} only has it as
// memory" (the mockup's line, now true); else "↑ {Name} can't see above here"
// (a window cut with nothing distilled yet, so it is never a lie).
//
// PURE on purpose (the crowdInline.js pattern): no React, no DOM, no clock.
// The pseudo-row never enters `results` or allActionsFlat (the journal, the
// board, the mood and "last spoke" all read those); the page inserts it at the
// feed's render step only, on the owner's desktop place page.

export const HORIZON_LINE_TYPE = 'ported_horizon';
export const HORIZON_ROW_ID = 'ported_horizon';
// the feed virtualizer's first guess (utils/crowdInline estimateFeedRowPx):
// one short printed line, like a crowd line
export const HORIZON_ROW_ESTIMATE_PX = 28;

export function horizonText(residentName, mem = false) {
  const name = String(residentName || '').trim() || 'Your resident';
  return mem === true ? `↑ above here, ${name} only has it as memory` : `↑ ${name} can't see above here`;
}

const toTs = (v) => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : NaN;
  if (!v) return NaN;
  return Date.parse(v);
};
const rowTs = (a) => {
  const t = toTs(a && a.created_at);
  if (Number.isFinite(t)) return t;
  return toTs(a && a.data && a.data.timestamp);
};
// Rows that only live on this page (a crowd line, an un-acked echo with a
// client clock, another pseudo-row) can never be the first row he saw.
const isRealRow = (a) => !!a && !a.isOptimistic && a.type !== HORIZON_LINE_TYPE && !String(a.id || '').startsWith('crowd_');

/**
 * Put the horizon row right above the first row the resident's prompt held.
 *   · boundary = { id, at } from the server; null/unparseable = nothing drawn
 *   · the boundary row's own id wins when it is loaded (exact); else the
 *     first real row in LIST order at or after `at` (the feed's order is the
 *     rounds' order and is never changed here)
 *   · nothing loaded above that slot = nothing drawn (a boundary older than
 *     the oldest loaded row, or equal to it, hides nothing the owner can see)
 * Returns the SAME array when there is nothing to draw (memo-friendly).
 */
export function insertHorizon(actions, boundary, row = null) {
  const feed = Array.isArray(actions) ? actions : [];
  if (!boundary || feed.length === 0) return feed;
  const at = toTs(boundary.at);
  const id = boundary.id != null ? String(boundary.id) : null;
  let slot = -1;
  if (id) slot = feed.findIndex((a) => isRealRow(a) && String(a.id) === id);
  if (slot === -1 && Number.isFinite(at)) {
    slot = feed.findIndex((a) => isRealRow(a) && Number.isFinite(rowTs(a)) && rowTs(a) >= at);
  }
  if (slot <= 0) return feed;
  if (!feed.slice(0, slot).some(isRealRow)) return feed;
  const line = row || horizonRow(boundary);
  return [...feed.slice(0, slot), line, ...feed.slice(slot)];
}

export function horizonRow(boundary) {
  const at = toTs(boundary && boundary.at);
  return {
    id: HORIZON_ROW_ID,
    type: HORIZON_LINE_TYPE,
    created_at: Number.isFinite(at) ? new Date(at).toISOString() : null,
    data: { boundary_id: boundary && boundary.id != null ? String(boundary.id) : null, mem: !!(boundary && boundary.mem === true) },
  };
}

/**
 * One-entry memo around insertHorizon, keyed on the feed's IDENTITY and the
 * boundary's VALUE (the page rebuilds the boundary object on every fetch).
 * The pseudo-row object itself is kept while the boundary holds, so a new
 * message below it never re-renders the horizon row.
 */
export function createHorizonCache() {
  let lastFeed = null;
  let lastKey = null;
  let lastRow = null;
  let lastOut = null;
  return (actions, boundary) => {
    const key = boundary ? `${boundary.id == null ? '' : boundary.id}|${boundary.at == null ? '' : boundary.at}|${boundary.mem === true ? 'm' : ''}` : '';
    if (actions === lastFeed && key === lastKey && lastOut) return lastOut;
    if (key !== lastKey) lastRow = boundary ? horizonRow(boundary) : null;
    lastFeed = actions;
    lastKey = key;
    lastOut = insertHorizon(actions, boundary, lastRow);
    return lastOut;
  };
}
